import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { getUser } from "../../redux/actions/users";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { deselectEvent } from "../../redux/actions/events";
import { formatBalance, getInitials } from "../../helpers/functions";
import { hasNoBalance, isPublicGalleryOn } from "../../helpers/eventPublication";
import UserMenu from "./userMenu";
import { RevProgress, RevSkeleton } from "../ds";
import logoDark from "./revopic-logo-dark.svg";

const Nav = (props) => {

    const { t, i18n } = useTranslation();
    const [retry, setRetry] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        let timeoutId;

        const fetchUserIfNeeded = () => {
            if (props.user?.balance === undefined) {
                if (!retry) {
                    // Intentar nuevamente después de un retraso si aún no se ha reintentado
                    setRetry(true);
                    timeoutId = setTimeout(() => {
                        props.getUser();
                    }, 10000);
                }
            }
        };

        fetchUserIfNeeded();

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line
    }, [props.user, retry]);

    const isEventsActive = props.location?.pathname?.includes('/events');
    const isGuideActive = props.location?.pathname?.startsWith('/guide');
    const initials = getInitials(props.user?.name);
    const formattedBalance = formatBalance(props.user?.balance, i18n.language);
    // Aún no ha llegado GET /user: balance solo lo derivan GET_USER y POST_USER, así que
    // «sin balance» es «sin dato», también con el pseudo-usuario que siembra
    // SET_USER_HAS_EVENTS_WITH_PRICING cuando getAllEvents gana la carrera. Si el GET cayó
    // (userFailed) se sale del esqueleto y se pinta el «—» de formatBalance/getInitials.
    const sinUsuario = props.user?.balance === undefined && !props.userFailed;
    // La pista mide el consumo sobre lo comprado (usedBalance / totalBalance de GET /user):
    // vacía sin consumos, llena cuando el saldo se agota. Decisión de Guillermo 2026-09-22;
    // sustituye al techo fijo de 5000 del artboard, que se leía como consumo sin serlo.
    // El pie sigue estimando ≈ 1 participante por crédito de saldo.
    const usedNum = Number(props.user?.usedBalance);
    const totalNum = Number(props.user?.totalBalance);
    const creditsKnown = Number.isFinite(usedNum) && Number.isFinite(totalNum) && totalNum > 0;
    const creditsProgress = creditsKnown
        ? Math.min(100, Math.max(0, Math.round((usedNum / totalNum) * 100)))
        : 0;
    const creditsTitle = creditsKnown
        ? t('nav.rail.creditsUsed', { used: formatBalance(usedNum, i18n.language), total: formatBalance(totalNum, i18n.language) })
        : undefined;

    // Sin saldo, el backend saca del listado de la app los eventos del dueño. Se avisa solo si
    // hay al menos uno propio publicado y los eventos ya han llegado (si no, no se sabe).
    const publishedHidden = !sinUsuario && hasNoBalance(props.user)
        && props.eventsFetched === true && (props.events || []).some(isPublicGalleryOn);

    return (
        <nav className="rev-rail" data-register="dark" aria-busy={sinUsuario || undefined}>
            <Link to="/events" className="rev-rail__logolink" onClick={() => props.deselectEvent()}>
                <img src={logoDark} alt="Revopic" className="rev-logo--on-dark rev-rail__logo" />
            </Link>
            <div className="rev-rail__nav">
                <Link
                    to="/events"
                    className={`rev-rail__item ${isEventsActive ? 'is-active' : ''}`}
                    onClick={() => props.deselectEvent()}
                >
                    <span className="material-icons rev-icon-sm">event</span>
                    {t('nav.rail.events')}
                </Link>
                <Link
                    to="/guide"
                    className={`rev-rail__item ${isGuideActive ? 'is-active' : ''}`}
                    onClick={() => props.deselectEvent()}
                >
                    <span className="material-icons rev-icon-sm">menu_book</span>
                    {t('nav.rail.guide')}
                </Link>
            </div>
            <div className="rev-rail__footer">
                <button
                    type="button"
                    className="rev-rail-card"
                    onClick={() => props.history.push('/account/credit/home')}
                >
                    <div className="rev-rail-card__row">
                        <span className="rev-rail-card__label">{t('account.sideNavigation.credit')}</span>
                        <span className="rev-num rev-rail-card__value">{sinUsuario ? <RevSkeleton kind="cifra" dark /> : formattedBalance}</span>
                    </div>
                    {sinUsuario
                        ? <RevSkeleton dark className="rev-rail-card__track" />
                        : <RevProgress className="rev-rail-card__track" value={creditsProgress} max={100} title={creditsTitle} aria-label={creditsTitle} />}
                    <div className="rev-rail-card__foot-row">
                        <span className="rev-rail-card__foot">{sinUsuario ? <RevSkeleton kind="texto" dark /> : t('nav.rail.creditsParticipants', { n: formattedBalance })}</span>
                        <span className="rev-rail-card__link">{t('nav.rail.view')}</span>
                    </div>
                    {publishedHidden && <span className="rev-rail-card__warn" role="note">{t('nav.rail.publishedHidden')}</span>}
                </button>
                <button
                    type="button"
                    className={`rev-rail-user ${menuOpen ? 'is-open' : ''}`}
                    onClick={() => setMenuOpen(o => !o)}
                >
                    {sinUsuario
                        ? <RevSkeleton kind="avatar" dark className="rev-rail-user__avatar" />
                        : <span className="rev-rail-user__avatar">{initials}</span>}
                    <span className="rev-rail-user__info">
                        <span className="rev-rail-user__name">{sinUsuario ? <RevSkeleton kind="texto" dark /> : (props.user?.name || '—')}</span>
                        <span className="rev-rail-user__role">{t('nav.rail.role')}</span>
                    </span>
                    <span className={`rev-rail-user__chevron ${menuOpen ? 'is-open' : ''}`}>
                        <span className="material-icons rev-icon-sm">expand_more</span>
                    </span>
                </button>
                {menuOpen && <UserMenu user={props.user} onNavigate={() => setMenuOpen(false)} />}
            </div>
        </nav>
    )
};

const mapStateToProps = state => (
    {
        user: state.usersReducer.user,
        loading: state.usersReducer.loading,
        userFailed: state.usersReducer.userFailed,
        events: state.eventsReducer.events,
        eventsFetched: state.eventsReducer.eventsFetched,
    }
);

const mapDispatchToProps =
{
    getUser,
    deselectEvent
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Nav));
